import React, { useRef } from 'react';
import { Unit } from '../../components/ServiceItem';
import { PatientProps } from '../../contexts/PatientContext';
import { EligibilityPlanProps } from '../../contexts/PaymentContext';
import { UnitDatesAvailable, useScheduleContext } from '../../contexts/ScheduleContext';
import { usePatientModule } from '../../hooks/usePatientModule';
import { useSchedulingMount } from '../../hooks/useSchedulingMount';
import { useSelectCoverage } from '../../hooks/useSelectCoverage';
import { CoverageProps, ProductItemProps, Rule } from '../../models/Product';
import { ScheduleItemProps } from '../../models/Schedule';
import { ScheduleSpecialist } from '../../models/ScheduleSpecialist';
import { useServiceContext } from '../../contexts/ServiceContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { FirebaseEvents } from '../../utils/enums/firebase-events';
import { Modals } from '../../contexts/ModalsContext';

type useServiceTypePageProps = {
  addPatientList: (patient: PatientProps) => PatientProps;
  changeProduct: (product: ProductItemProps) => void;
  eligibilityPlans: EligibilityPlanProps[];
  setEligibilityPlans: (plans: EligibilityPlanProps[]) => void;
  goTo: (route: number, state?: any) => void;
  changeSchedule: (schedule: ScheduleItemProps) => ScheduleItemProps;
  selectedSchedule: ScheduleItemProps | undefined;
  openModal: (modal: keyof typeof Modals) => void;
  scheduleSpecialistSelected: ScheduleSpecialist;
  setScheduleSpecialistSelected: React.Dispatch<React.SetStateAction<ScheduleSpecialist>>;
  changeScheduleSpecialist: (schedule: ScheduleSpecialist) => void;
  loadingChangeProduct: boolean;
  addUnits: (units: Unit[]) => void;
  addUnitDatesAvailable: (unitTimes: UnitDatesAvailable) => void;
  patientSelected: PatientProps | undefined;
  productRules: Rule[];
};

export const useServiceTypePage = ({
  addPatientList,
  changeProduct,
  eligibilityPlans,
  setEligibilityPlans,
  changeSchedule,
  goTo,
  selectedSchedule,
  openModal,
  changeScheduleSpecialist,
  scheduleSpecialistSelected,
  setScheduleSpecialistSelected,
  loadingChangeProduct,
  addUnits,
  addUnitDatesAvailable,
  patientSelected,
  productRules
}: useServiceTypePageProps) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const { genericModal, isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible } = usePatientModule({
    addPatientList,
    changeProduct,
    eligibilityPlans,
    setEligibilityPlans,
    goTo,
    changeSchedule,
    selectedSchedule,
    openModal,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  });
  const useScheduling = useSchedulingMount({ changeSchedule, selectedSchedule });
  const [showModalServiceUnavailable, setModalServiceUnavailable] = React.useState(false);

  const {
    coverageUnit,
    unitDatesAvailable,
    openToScheduleUnits,
    selectedCoverage,
    closedToScheduleUnits,
    handleSelectedCoverage,
    setSpecificUnitsResources,
    loading: loadingUnitDates,
    resetCoverageSelection,
    selectedUnit,
    setUnitToSchedule,
    resetCoverageSelected
  } = useSelectCoverage({
    handleModalProps: genericModal.handleModalProps,
    changeSchedule,
    openModal,
    scheduleSelected: selectedSchedule,
    addUnitDatesAvailable,
    productRules,
    submitButtonRef,
    setModalServiceUnavailable: setModalServiceUnavailable
  });

  const loading = loadingChangeProduct || loadingUnitDates;

  React.useEffect(() => {
    addUnits(openToScheduleUnits);
  }, [openToScheduleUnits]);

  const handleRemoveResource = async () => {
    if (useScheduling.schedule) {
      resetCoverageSelection();
      resetCoverageSelected();
      useScheduling.addResource(false);
      useScheduling.updateSchedule();
    }
    genericModal.handleModalProps({ visible: false });
  };

  const { logEvent } = useAnalytics();

  const { selectedItem } = useScheduleContext();
  const { selectedService } = useServiceContext();

  const selectCoverage = (coverage: CoverageProps) => {
    logEvent({
      eventName: FirebaseEvents.schedule_selected_attendance_type,
      eventParams: {
        product_id: selectedItem?.productId,
        product_name: selectedItem?.productName,
        service_id: selectedService.id,
        service_name: selectedService.name,
        attendanceType: coverage.care_type
      }
    });

    handleSelectedCoverage({ coverage, patientSelected });
  };

  React.useEffect(() => {
    if (useScheduling.schedule.resource) useScheduling.addResource(true);
  }, []);

  return {
    loading,
    selectCoverage,
    handleRemoveResource,
    selectedUnit,
    coverageUnit,
    selectedCoverage,
    openToScheduleUnits,
    closedToScheduleUnits,
    setUnitToSchedule,
    schedule: useScheduling.schedule,
    genericModal,
    resetCoverageSelection: () => {
      resetCoverageSelection();
      resetCoverageSelected();
    },
    setSpecificUnitsResources,
    isModalSystemUnavailableVisible,
    setIsModalSystemUnavailableVisible,
    submitButtonRef,
    showModalServiceUnavailable,
    setShowModalServiceUnavailable: setModalServiceUnavailable
  };
};
